import Router, { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RESUME_ID_QUERY } from '@glass/common/modules/pages/queryParams';
import ResumeBuilderRouter from '@glass/web/modules/resumes/router/ResumeBuilderRouter';

import openConfirmationDialogAction from '@glass/shared/modules/globalDialog/openConfirmationDialogAction';
import {
  createSelectShouldPromptActiveResume,
  declineRecentResumesAction,
  selectMostRecentResumeId,
} from '@glass/shared/modules/resumes/resumesReducer';
import createSelectNextResumeBuilderParams from '@glass/shared/modules/resumes/selectors/createSelectNextResumeBuilderParams';
import Tracking from '@glass/shared/modules/tracking/Tracking';

const promptActiveResumeAction =
  () =>
  (dispatch, getState, { tracking }) => {
    const mostRecentResumeId = selectMostRecentResumeId(getState());
    tracking.track(Tracking.EVENTS.ACTIVE_RESUME, { action: Tracking.ACTIONS.START });
    const createHandleEnd = (label) => () => {
      tracking.track(Tracking.EVENTS.ACTIVE_RESUME, {
        action: Tracking.ACTIONS.END,
        label,
      });
      dispatch(declineRecentResumesAction(mostRecentResumeId));
    };

    dispatch(
      openConfirmationDialogAction({
        title: 'Continue with your previous $(resumeWords.singular)?',
        message: `Would you like to continue working on your $(resumeWords.singular)?`,
        confirmText: 'Continue',
        onConfirm: () => {
          tracking.track(Tracking.EVENTS.ACTIVE_RESUME, { action: Tracking.ACTIONS.SUCCESS });
          const nextStepParams = createSelectNextResumeBuilderParams({ id: mostRecentResumeId })(
            getState(),
          );
          return Router.replace(ResumeBuilderRouter.getResumeBuilderUrl(nextStepParams));
        },
        onCancel: createHandleEnd('cancel'),
        onClose: createHandleEnd('close'),
      }),
    );
  };

const useActiveResume = ({ resume, skip } = {}) => {
  const dispatch = useDispatch();
  const {
    query: { [RESUME_ID_QUERY]: resumeIdQuery },
    isReady,
  } = useRouter();

  const resumeId = resume?.id || resumeIdQuery;
  const selectPromptActiveResume = useMemo(
    () => createSelectShouldPromptActiveResume(resumeId),
    [resumeId],
  );
  const shouldPromptActiveResume = useSelector(selectPromptActiveResume);

  useEffect(() => {
    if (!resumeId && isReady && !skip && shouldPromptActiveResume) {
      dispatch(promptActiveResumeAction());
    }
  }, [skip, resumeId, shouldPromptActiveResume, dispatch, isReady]);
};

export default useActiveResume;
